'use client';

import { signIn } from 'next-auth/react';
import { useState } from 'react';

function getCallbackUrl(): string {
  let defaultPage = 'ledger';
  try {
    const raw = localStorage.getItem('muratum_prefs');
    if (raw) {
      const prefs = JSON.parse(raw);
      if (prefs.defaultPage) defaultPage = prefs.defaultPage;
    }
  } catch {}

  const migration: Record<string, string> = { dashboard: 'ledger', ledger: 'ledger', 'cashflow-analysis': 'cashflow', networth: 'networth-overview' };
  defaultPage = migration[defaultPage] ?? defaultPage;

  const routeMap: Record<string, string> = {
    ledger:             'ledgers?tab=earnings-expenses',
    investments:        'ledgers?tab=investments',
    'networth-overview': 'networth?tab=overview',
    'networth-history':  'networth?tab=history',
  };
  const path = routeMap[defaultPage] ?? defaultPage;

  let locale = 'en';
  try {
    const match = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
    if (match) locale = match[1];
  } catch {}

  return `/${locale}/${path}`;
}

export default function LoginPage() {
  const [loading, setLoading] = useState(false);

  const handleSignIn = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await signIn('google', { callbackUrl: getCallbackUrl() });
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className="bg-surface min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm flex flex-col items-center gap-6">

        {/* Logo / title */}
        <div className="flex flex-col items-center gap-3">
          <img src="/muratum-logo.png" alt="muratum" className="w-auto" style={{ height: '43.2px' }} />
          <span className="text-xs text-gray-400 tracking-widest uppercase">Personal Finance</span>
        </div>

        <p className="text-sm text-gray-500 text-center">
          Sign in to check your personal finance situation
        </p>

        {/* Google sign-in button */}
        <button
          onClick={handleSignIn}
          disabled={loading}
          className="flex items-center gap-3 w-full justify-center px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <svg className="animate-spin w-4 h-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <g fill="none" fillRule="evenodd">
                <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </g>
            </svg>
          )}
          {loading ? 'Signing in…' : 'Sign in with Google'}
        </button>

        <p className="text-xs text-gray-400 text-center">
          You&apos;ll be asked to grant access to Google Sheets so Muratum can sync your investment prices.
        </p>
      </div>
    </div>
  );
}
